import React, { useContext, useEffect, useState, useRef } from 'react';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import Page from '../../components/page';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import CardFile from '../../components/card-file';
import SkeletonLoading from '../../components/skeleton-loading';
import { toast } from 'react-toastify';
import NoResults from '../../components/no-result';
import { buildOrgLevelBreadcrumbs } from '../../utils/breadcrumbUtils';
import { Col, Row } from 'react-bootstrap';
import {
  Container,
  DocumentContainer,
  LoadingContainer,
  Loader,
  HorizontalSplit,
  NoResultsContainer,
  FileInput,
  FileInputGroup,
  SendButton,
  DeleteButton,
} from './styles';
import { IoLibraryOutline } from 'react-icons/io5';
import { BsTrash } from 'react-icons/bs';
import { useConfirmToast } from '../../hooks/useConfirmToast';

const Uploads = () => {
  const { currentOrganization } = useContext(ContextUser);
  const [loading, setLoading] = useState(true);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [fileUpload, setFileUpload] = useState(null);
  const [selected, setSelected] = useState([]);
  const [fileError, setFileError] = useState('');

  const fileInputRef = useRef(null);
  const { confirm } = useConfirmToast();

  useEffect(() => {
    fetchOrgDocs();
  }, []);

  // Configurar breadcrumbs quando a organização estiver disponível
  useEffect(() => {
    if (currentOrganization?.id) {
      const breadcrumbs = buildOrgLevelBreadcrumbs(
        currentOrganization,
        'Biblioteca',
        '/library'
      );
      setBreadcrumbs(breadcrumbs);
    }
  }, [currentOrganization]);

  async function fetchOrgDocs() {
    setLoading(true);
    const orgId = currentOrganization.id;

    await api
      .get(`/orgs/${orgId}/documents`)
      .then((r) => {
        setUploadFiles(r.data);
        setSelected([]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        const msg = 'Arquivo excede 50MB. Selecione um arquivo menor.';
        setFileError(msg);
        toast.error(msg);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setFileError('');
      setFileUpload(file);
    }
  };

  const handleUpload = async () => {
    const orgId = currentOrganization.id;

    if (!fileUpload) {
      return;
    }
    const formData = new FormData();
    formData.append('file', fileUpload);

    try {
      const response = await api.post(`/orgs/${orgId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadFiles([
        ...uploadFiles,
        { name: fileUpload.name, url: response.data.url },
      ]);

      toast.success('Upload realizado com sucesso');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFileUpload(null);
      setFileError('');
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao realizar upload';
      toast.error(message);
    }
  };

  const toggleSelect = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleDeleteSelected = () => {
    if (selected.length === 0) return;

    confirm(
      'Os arquivos selecionados serão excluídos permanentemente. Deseja continuar?',
      async () => {
        try {
          const orgId = currentOrganization.id;
          await api.delete(`/orgs/${orgId}/documents`, { data: { files: selected } });
          toast.success('Arquivos removidos com sucesso');
          fetchOrgDocs();
        } catch (error) {
          toast.error('Erro ao remover arquivos');
        }
      },
      null,
      { confirmText: 'Sim, excluir!', cancelText: 'Cancelar', variant: 'danger' }
    );
  };

  const HeaderDeleteButton = selected.length > 0 ? (
    <DeleteButton key='btnDeleteSelected' onClick={handleDeleteSelected}>
      <BsTrash style={{ marginRight: 8 }} /> Excluir selecionados ({selected.length})
    </DeleteButton>
  ) : null;

  return (
    <>
      <Breadcrumbs id='breadcrumbs-library' path={breadcrumbs} />
      <Page id='page-library' title={'Biblioteca'} description="Repositório de documentos institucionais e materiais de apoio da prefeitura." icon={<IoLibraryOutline />} sideAction={[HeaderDeleteButton]}>
        {loading ? (
        <Container id='container-loading'>
          <SkeletonLoading id='library-skel-input' height='40px' width='60%' />
          <HorizontalSplit />
          <Row id='library-skel-grid' style={{ width: '100%' }} className='g-3'>
            {[0,1,2,3,4,5].map((i) => (
              <Col id={`skeleton-library-${i}`} md={4} key={`sk-${i}`}>
                <SkeletonLoading height='150px' />
              </Col>
            ))}
          </Row>
        </Container>
      ) : (
        <Container id='container'>
          <FileInputGroup className='mt-3'>
            <FileInput type='file' onChange={handleFileUpload} ref={fileInputRef} />
            <SendButton onClick={handleUpload} disabled={!fileUpload || !!fileError}>Enviar</SendButton>
          </FileInputGroup>
          <div id='library-file-hint' style={{ marginTop: 6, fontSize: 12, color: '#6c757d' }}>
            Tamanho máximo permitido: 50MB.
          </div>
          {fileError && (
            <div id='library-file-error' style={{ marginTop: 4, fontSize: 12, color: '#dc3545' }}>
              {fileError}
            </div>
          )}

          <HorizontalSplit />
          <DocumentContainer id='document-container'>
            {uploadFiles.length > 0
              ? uploadFiles.map((doc) => (
                  <CardFile
                    id={`card-file-${doc.name}`}
                    key={doc.url}
                    doc={doc}
                    selectable
                    selected={selected.includes(doc.name)}
                    onToggleSelect={toggleSelect}
                  />
                ))
              : !loading && (
                  <NoResultsContainer id='no-results-container'>
                    <NoResults
                      id='no-results'
                      message='Nenhum item na biblioteca'
                      searchText=''
                    />
                  </NoResultsContainer>
                )}
          </DocumentContainer>
        </Container>
      )}
      </Page>
    </>
  );
};

export default Uploads;
